/* eslint-disable @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-floating-promises, @typescript-eslint/prefer-nullish-coalescing */
import LoanCard from "components/loan-card/LoanCard";
import { MyLoanInfo, useGetMyLoansQuery } from "pages/application/applicationService";
import { ConsentTypes, useSetConsentMutation } from "pages/authentication/userService";
import "./loans-list.scss";
import Footer from "components/footer/Footer";
import { getCurrentUserId } from "features/authSlice";
import { isPostLosLoan, setIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import { useLoanSelectionRouter } from "hooks/useLoanSelectionRouter";
import { useNavigateToPostAuth } from "hooks/useNavigateToPostAuth";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { formatDate } from "utilities/date";
import { useCallback, useEffect } from "react";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import noLoanImage from "assets/images/v2/no-loan.svg";
import RedesignAnnouncementModal from "pages/announcements/re-design/re-design-modal";
import { ENCOMPASS_LOS_TYPE } from "utilities/loan-sidebar/constants";
import { getLoanKey } from "utilities/loan-sidebar/getLoanKey";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useLoanCentricSidebarNavEnabled } from "hooks/useLoanCentricSidebarNavEnabled";

const pathsToShowMyLoans = ["/application", "/dashboard"];

const resolveLosType = (loan: MyLoanInfo): typeof ENCOMPASS_LOS_TYPE | "vesta" => {
  const raw = (loan as MyLoanInfo & { losType?: string }).losType;
  return raw === "vesta" ? "vesta" : ENCOMPASS_LOS_TYPE;
};

export default function LoansList() {
  const userId = useSelector(getCurrentUserId);
  const { data, isLoading, isFetching } = useGetMyLoansQuery(userId);
  const [sendRequest] = useSetConsentMutation();
  const location = useLocation();
  const dispatch = useDispatch();
  const routeLoanSelection = useLoanSelectionRouter();

  const selectLoan = useCallback(
    (loan: MyLoanInfo) => {
      routeLoanSelection({
        loan,
        losType: resolveLosType(loan),
        loanKey: getLoanKey(loan),
        source: "my_loans_card",
      });
    },
    [routeLoanSelection],
  );
  const navigateToPostAuth = useNavigateToPostAuth();
  const pathsToShowMyLoans = ["/application", "/dashboard"];
  const isDesktop = useDesktopChecker();
  const loanCentricSidebarNavEnabled = useLoanCentricSidebarNavEnabled();
  // Figma: GlobalHeader shows "Loans" on phone/tablet — hide duplicate body title.
  const showPageTitle = isDesktop || !loanCentricSidebarNavEnabled;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (data) {
      dispatch(setIsLanguageToggleLocked(data.length > 0 && data.every((loan) => isPostLosLoan(loan.loanNumber))));
      if (data.length === 1) {
        if (
          !(
            location.state != null &&
            typeof location.state === "object" &&
            "previousPath" in location.state &&
            pathsToShowMyLoans.some((str) => location.state.previousPath.includes(str))
          )
        ) {
          selectLoan(data[0]);
        }
      } else if (data.length > 1) {
        data.forEach((myLoan) => {
          sendRequest({
            id: myLoan.nhDealApplicationId,
            consent: {
              userId: userId,
              nhDealApplicationId: myLoan.nhDealApplicationId,
              prospectId: myLoan.prospectId,
              consentTypes: [ConsentTypes.PrivacyPolicy, ConsentTypes.TermsOfUse],
            },
          });
        });
      }
    }
  }, [data, dispatch, isLoading, location.state, selectLoan, sendRequest, userId]);

  const handleLoanCardClick = (index: number) => {
    if (data?.[index]) {
      selectLoan(data[index]);
    }
  };
  const hasLoans = (data?.length ?? 0) > 0;

  if (isFetching || isLoading) {
    return <Overlay />;
  } else if (data?.length === 1) {
    return <RedesignAnnouncementModal />;
  } else
    return (
      <>
        <RedesignAnnouncementModal />
        <div className="loans-section">
          <div className={`loans-section__wrapper ${hasLoans ? "" : "no-loans-full-width"}`}>
            {showPageTitle && (
              <div className="loans-section__header">
                <span className="loans-section__header-title"> Loans</span>
              </div>
            )}

            {hasLoans ? (
              <div className="loans-section__content">
                <div className="loans-section__header-subtitle">Select a loan below to view your application details.</div>
                <div className="loans-section__card-container">
                  {data?.map((item, index) => {
                    const {
                      property,
                      loanPurpose,
                      loanAmount,
                      status,
                      startedDate,
                      documentStatus,
                      loanNumber,
                      nhDealApplicationId,
                      applicationNumber,
                    } = item;
                    return (
                      <div className="loans-section__card" key={nhDealApplicationId}>
                        <LoanCard
                          property={property}
                          loanPurpose={loanPurpose}
                          loanAmount={loanAmount}
                          status={status}
                          startedDate={formatDate(startedDate)}
                          documentStatus={documentStatus}
                          handleLoanCardClick={handleLoanCardClick}
                          index={index}
                          loanNumber={loanNumber}
                          nhDealApplicationId={nhDealApplicationId}
                          applicationNumber={applicationNumber}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="loans-section__header-subtitle">Select a loan below to view your application details.</div>
                <div className="loans-section__no-loans">
                  <div className="loans-section__no-loans__content">
                    <div className="loans-section__no-loans__icon">
                      <img src={noLoanImage} alt="no-loan" />
                    </div>
                    <div className="loans-section__no-loans__content-text">
                      <div className="loans-section__no-loans__content-text__title">No Loans Available.</div>
                      <div className="loans-section__no-loans__content-text__description">
                        It looks like you don't have any active loans. If you have questions, feel free to connect with your Loan Officer or
                        <br />
                        give us a call at{" "}
                        <a className="loans-section__no-loans__phone-link" href="tel:8772144431">
                          (877) 214-4431
                        </a>
                        .
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="my-loans-footer">
          <Footer />
        </div>
      </>
    );
}
